/**
 * Adding the device's staple foods to a search the server could not answer (food staples plan 4c,
 * #5625, Requirements C3 and C4).
 *
 * WHY ONLY WHEN THE SERVER DID NOT ANSWER. While the server answers, it already returns the staples
 * — they are in `global_foods` and ranked above other sources — so adding them again would buy
 * nothing and cost a pass over every staple name and alias on every search. It is when the server
 * is unreachable or failed that the list would otherwise be nothing but the foods this person has
 * used before, with no plain "chicken breast" in it at all. That is the case this whole plan exists
 * for.
 *
 * These rules are in their own module so they can be unit-tested: `foods.ts` is an Angular component
 * and the client's unit tests are plain node tests, which is why `foods-filters.ts`,
 * `food-pick-guard.ts` and `staple-result-row.ts` are separate too.
 */

import { Food, FoodHeader } from '@cf-shared/data-model/food';
import { stapleRowsToAdd } from '@cf-shared/food-search/staple-merge';

/** what this module needs from the page to rebuild a row it refreshes */
export interface OfflineStapleDeps {
  /** turns a curated staple document into the row the list shows, exactly as a fresh one is built */
  buildRow: (staple: Food) => FoodHeader;
}

/**
 * The headers to search, with the device's staples added when the server contributed nothing.
 *
 * ⚠️ MODIFIES THE HEADERS IT IS GIVEN. When the person already has their own copy of a staple in the
 * list, that header is updated in place — see `mergeStapleIntoTheirRow` in `shared/src/food-search/staple-merge.ts` for exactly what changes and why.
 * The returned array is a new one, but the objects in it are the caller's.
 *
 * WHERE THE CALLER MUST PUT THE RESULT: into the list that still has to go through the filters. The
 * staples are ordinary foods as far as the type filter, Hide Unverified and the list cap are
 * concerned, and a staple that slipped past them would be a staple the person had asked not to see.
 *
 * The ranking then decides which of them survive: a device header whose name answers none of the
 * typed words scores 0 and is dropped, so adding every staple here does not mean showing every
 * staple.
 *
 * NOT ON A BARCODE SEARCH, and this is the one condition that is not about cost. The ranking's
 * "drop a device food that answered nothing" rule is switched OFF for a barcode search, because a
 * barcode answers no typed word — so nothing would drop the staples and the list would fill with
 * arbitrary foods, any of which could be logged with one tap, when the honest answer is that the
 * barcode was not found.
 *
 * @param headers        what the search found, before filtering
 * @param staples        every staple the device holds, as search headers
 * @param serverAnswered whether the server search contributed; when it did, the staples are left out
 * @param isBarcodeSearch whether this search is for a barcode, which the staples cannot answer
 */
export function withOfflineStaples(
  headers: FoodHeader[],
  staples: FoodHeader[],
  serverAnswered: boolean,
  isBarcodeSearch: boolean,
  deps: OfflineStapleDeps,
): FoodHeader[] {
  if (serverAnswered || isBarcodeSearch || staples.length === 0) {
    return headers;
  }
  return [...headers, ...staplesToAdd(headers, staples, deps)];
}

/**
 * The staples that are not already in the list, merging the curated values into the rows that are.
 *
 * The rule is shared with the scanners (`shared/src/food-search/staple-merge.ts`) so the two cannot drift: their
 * row is kept because it carries the like mark and the last-used date the ranking weights by and
 * two of this page's filters decide by, and the curated values are merged ONTO it.
 */
function staplesToAdd(headers: FoodHeader[], staples: FoodHeader[], deps: OfflineStapleDeps): FoodHeader[] {
  return stapleRowsToAdd(headers, staples, deps);
}
